import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { usePermissions } from '@/shared/permissions';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useCustomerGroups,
  usePriceLifecycleAudit,
  usePriceLists,
  usePriceResolution,
  useProductPriceLines,
  useRetailStores,
  useSalesChannels,
} from '../../hooks';
import { formatDateTime, formatPrice, formatPercent } from '../../lib/format';
import {
  governingLine,
  groupBySlot,
  isBaseSlot,
  negativeLine,
  pendingLine,
  scheduledLine,
  summarizeBase,
  unitAmountOf,
} from '../../lib/priceSlots';
import type { PriceSlot } from '../../lib/priceSlots';
import type { ToastSeverity } from '../../lib/useToast';
import type { CustomerGroupResponse } from '../../model/customerGroupTypes';
import type { PriceListResponse } from '../../model/priceListTypes';
import type { PriceLineResponse, SalesChannelResponse } from '../../model/types';
import type { PriceCellEditorContext } from './PriceLineEditorForm';
import { PriceLineEditorForm } from './PriceLineEditorForm';

export interface ProductPricesDrawerProps {
  open: boolean;
  onClose: () => void;
  product: { id: string; name?: string };
  /** RBAC (design spec §4, adr-pricing-025 d9): manage → every edit action; view-only → the level-1
   * overview stays fully readable, level 2 opens read-only under a lock notice. Callers only render
   * this drawer once `catalog.pricing.view` already holds (the R5 tile fix), so no separate
   * `canView` prop exists here. */
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

type Level =
  | { view: 'overview' }
  | { view: 'editor'; context: PriceCellEditorContext; targetLine?: PriceLineResponse; title: string };

const NONE_VALUE = '__none__';

// ── Small, presentation-only helpers (this file's own copies — `stateColor` mirrors the same tiny
// helper PriceGridPanel.tsx and PriceListDetailPage.tsx each already keep locally rather than
// sharing, the established convention in this feature) ──────────────────────────────────────────

type ChipColor = 'success' | 'warning' | 'error' | 'default';

function stateColor(state: string): ChipColor {
  if (state === 'ACTIVE') return 'success';
  if (state === 'REQUIRES_APPROVAL') return 'warning';
  if (state === 'BLOCKED') return 'error';
  return 'default';
}

const SUSPICION_KEYS: Record<string, string> = {
  DROP_TO_ZERO: 'productEdit.pricesDrawer.suspicion.dropToZero',
  SUSPICIOUS_DROP: 'productEdit.pricesDrawer.suspicion.suspiciousDrop',
  EXCESSIVE_DISCOUNT: 'productEdit.pricesDrawer.suspicion.excessiveDiscount',
  LARGE_DEVIATION: 'productEdit.pricesDrawer.suspicion.largeDeviation',
};

/** Translates a known `suspicionReason` code; falls through to the raw `reason`/code otherwise
 * (design spec §8.3 point 4). */
function suspicionLabel(
  t: (key: string) => string,
  suspicionReason?: string,
  reason?: string,
): string | undefined {
  if (suspicionReason && SUSPICION_KEYS[suspicionReason]) return t(SUSPICION_KEYS[suspicionReason]);
  return reason ?? suspicionReason;
}

/** The validity-window sentence: "in effect from {from}" alone, "{from} to {to}", "until {to}" or
 * "no end date" (design spec §8.3 point 1) — the `.from` and `.open` keys compose for the common
 * "started, no end date" case. */
function validityText(t: (key: string, vars?: Record<string, unknown>) => string, line: PriceLineResponse): string {
  const from = line.validFrom ? formatDateTime(line.validFrom) : undefined;
  const to = line.validTo ? formatDateTime(line.validTo) : undefined;
  if (from && to) return t('productEdit.pricesDrawer.base.validity.range', { from, to });
  if (from) {
    return `${t('productEdit.pricesDrawer.base.validity.from', { from })}, ${t('productEdit.pricesDrawer.base.validity.open')}`;
  }
  if (to) return t('productEdit.pricesDrawer.base.validity.until', { to });
  return t('productEdit.pricesDrawer.base.validity.open');
}

/** The "+N tiers" chip with a tooltip listing each wholesale row — shared by the base summary
 * (§8.3) and every "other prices" row (§8.5), the only tier-count copy the design spec names. */
function TiersChip({ line }: { line: PriceLineResponse }) {
  const { t } = useTranslation('catalog');
  const extra = line.tiers.filter((tier) => tier.minQuantity > 1);
  if (extra.length === 0) return null;
  return (
    <Tooltip
      title={
        <Stack spacing={0.25}>
          {extra.map((tier) => (
            <span key={tier.minQuantity}>
              {t('productEdit.pricesDrawer.others.tierLine', {
                qty: tier.minQuantity,
                price: formatPrice(tier.amount),
              })}
            </span>
          ))}
        </Stack>
      }
    >
      <Chip
        size="small"
        variant="outlined"
        label={t('productEdit.pricesDrawer.others.tiers', { count: extra.length })}
      />
    </Tooltip>
  );
}

function gridHref(slot: PriceSlot): string {
  const scope = slot.priceListCode
    ? `list:${slot.priceListCode}`
    : slot.channelCode
      ? `channel:${slot.channelCode}`
      : 'base';
  const group = slot.customerGroupCode ? `&group=${encodeURIComponent(slot.customerGroupCode)}` : '';
  return `/catalog/pricing?tab=grid&scope=${encodeURIComponent(scope)}${group}`;
}

function approvalsHref(productId: string): string {
  return `/catalog/pricing?tab=approvals&productId=${encodeURIComponent(productId)}`;
}

function auditHref(productId: string): string {
  return `/catalog/pricing?tab=audit&productId=${encodeURIComponent(productId)}`;
}

function batchHref(batchId: string): string {
  return `/catalog/pricing?tab=bulk&view=batches&batch=${encodeURIComponent(batchId)}`;
}

/** Truncates a UUID for inline display — the same 8-char + ellipsis convention
 * `PublicationRunDetailDrawer` already uses for a long id shown next to other text. */
function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

/** A slot's display name, resolved from its owning registry with a code fallback (design spec
 * §8.5 — "code fallback in mono"), plus whether the matched entry is inactive. */
function slotDisplay(
  slot: PriceSlot,
  channels: SalesChannelResponse[],
  lists: PriceListResponse[],
  groups: CustomerGroupResponse[],
): { channelName?: string; listName?: string; groupName?: string; inactive: boolean } {
  const channel = slot.channelCode ? channels.find((c) => c.code === slot.channelCode) : undefined;
  const list = slot.priceListCode ? lists.find((l) => l.code === slot.priceListCode) : undefined;
  const group = slot.customerGroupCode ? groups.find((g) => g.code === slot.customerGroupCode) : undefined;
  const inactive = [channel, list, group].some((entry) => entry && entry.active === false);
  return {
    channelName: slot.channelCode ? (channel?.name ?? slot.channelCode) : undefined,
    listName: slot.priceListCode ? (list?.name ?? slot.priceListCode) : undefined,
    groupName: slot.customerGroupCode ? (group?.name ?? slot.customerGroupCode) : undefined,
    inactive,
  };
}

/** The "Applies to" cell text (design spec §8.5). */
function slotLabel(
  t: (key: string, vars?: Record<string, unknown>) => string,
  slot: PriceSlot,
  display: ReturnType<typeof slotDisplay>,
): string {
  const suffix = display.inactive ? ` ${t('productEdit.pricesDrawer.others.slot.inactiveSuffix')}` : '';
  if (slot.priceListCode) return t('productEdit.pricesDrawer.others.slot.list', { name: display.listName }) + suffix;
  if (slot.channelCode && slot.customerGroupCode) {
    return (
      t('productEdit.pricesDrawer.others.slot.channelGroup', {
        channel: display.channelName,
        group: display.groupName,
      }) + suffix
    );
  }
  if (slot.channelCode) return t('productEdit.pricesDrawer.others.slot.channel', { name: display.channelName }) + suffix;
  return t('productEdit.pricesDrawer.others.slot.group', { name: display.groupName }) + suffix;
}

/** Ladder-order rank for §8.5's row ordering: list → channel+group → channel → group. */
function slotRank(slot: PriceSlot): number {
  if (slot.priceListCode) return 0;
  if (slot.channelCode && slot.customerGroupCode) return 1;
  if (slot.channelCode) return 2;
  return 3;
}

/** The exact editor context for a non-base slot, and the level-2 header title (design spec §8.2). */
function contextAndTitle(
  t: (key: string, vars?: Record<string, unknown>) => string,
  slot: PriceSlot,
  display: ReturnType<typeof slotDisplay>,
): { context: PriceCellEditorContext; title: string } {
  if (slot.priceListCode) {
    return {
      context: { type: 'list', code: slot.priceListCode, name: display.listName ?? slot.priceListCode },
      title: t('productEdit.pricesDrawer.others.slot.list', { name: display.listName }),
    };
  }
  const group = slot.customerGroupCode ? { code: slot.customerGroupCode, name: display.groupName } : undefined;
  if (slot.channelCode) {
    return {
      context: { type: 'channel', code: slot.channelCode, name: display.channelName, group },
      title: slot.customerGroupCode
        ? t('productEdit.pricesDrawer.others.slot.channelGroup', {
            channel: display.channelName,
            group: display.groupName,
          })
        : t('productEdit.pricesDrawer.others.slot.channel', { name: display.channelName }),
    };
  }
  return {
    context: { type: 'base', group },
    title: t('productEdit.pricesDrawer.others.slot.group', { name: display.groupName }),
  };
}

// ── Section: "Base price" (design spec §8.3) ────────────────────────────────────────────────────

function BaseSection({
  lines,
  canManage,
  onEdit,
  productId,
}: {
  lines: PriceLineResponse[];
  canManage: boolean;
  onEdit: (target: PriceLineResponse | undefined, triggerId: string) => void;
  productId: string;
}) {
  const { t } = useTranslation('catalog');
  const now = new Date();
  const summary = summarizeBase(lines, now);
  const { governing, scheduled } = summary;
  const pendingOrNegative = summary.pending ?? summary.negative;

  const price = unitAmountOf(governing);
  const cost = governing?.costAmount != null ? Number(governing.costAmount) : undefined;
  const belowCost = cost != null && price != null && price > 0 && cost >= price;
  const costParts: ReactNode[] = [];
  if (governing?.costAmount != null) {
    costParts.push(t('productEdit.pricesDrawer.base.cost', { cost: formatPrice(cost) }));
  }
  if (cost != null && price != null && price > 0) {
    costParts.push(t('productEdit.pricesDrawer.base.margin', { margin: formatPercent(((price - cost) / price) * 100) }));
    if (cost > 0) {
      costParts.push(
        t('productEdit.pricesDrawer.base.markup', { markup: formatPercent(((price - cost) / cost) * 100) }),
      );
    }
  }
  if (governing?.minSaleAmount != null) {
    costParts.push(t('productEdit.pricesDrawer.base.minSale', { amount: formatPrice(Number(governing.minSaleAmount)) }));
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="overline" color="text.secondary" component="h3" sx={{ letterSpacing: '0.08em' }}>
          {t('productEdit.pricesDrawer.base.title')}
        </Typography>
        {canManage ? (
          <Button
            size="small"
            variant="outlined"
            data-edit-trigger-id="base-governing"
            onClick={() => onEdit(governing, 'base-governing')}
          >
            {governing ? t('productEdit.pricesDrawer.base.edit') : t('productEdit.pricesDrawer.base.set')}
          </Button>
        ) : null}
      </Stack>

      {!governing && !scheduled && !pendingOrNegative ? (
        <Alert severity="warning" variant="outlined">
          {t('productEdit.pricesDrawer.base.none')}
        </Alert>
      ) : null}

      {governing ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {formatPrice(price)}
          </Typography>
          {governing.compareAtAmount != null ? (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              {t('productEdit.summary.price.wasPrice', { price: formatPrice(governing.compareAtAmount) })}
            </Typography>
          ) : null}
          <Chip size="small" label={governing.state} color={stateColor(governing.state)} />
          <Typography variant="body2" color="text.secondary">
            {validityText(t, governing)}
          </Typography>
          <TiersChip line={governing} />
        </Stack>
      ) : null}

      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {costParts.length > 0 ? (
          <Typography variant="body2" color="text.secondary">
            {costParts.join(' · ')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('productEdit.pricesDrawer.base.costNone')}
          </Typography>
        )}
        {belowCost ? (
          <Typography
            variant="body2"
            color="warning.main"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <WarningAmberRoundedIcon fontSize="inherit" />
            {t('productEdit.pricesDrawer.base.belowCost')}
          </Typography>
        ) : null}
      </Stack>

      {scheduled ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ScheduleRoundedIcon fontSize="small" color="info" />
          <Typography variant="body2">
            {t('productEdit.summary.price.scheduled', {
              date: formatDateTime(scheduled.validFrom),
              price: formatPrice(unitAmountOf(scheduled)),
            })}
          </Typography>
          <Chip size="small" color="info" variant="outlined" label={t('productEdit.pricesDrawer.base.scheduledChip')} />
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            {t('productEdit.pricesDrawer.base.scheduledHint')}
          </Typography>
          {canManage ? (
            <Button size="small" data-edit-trigger-id="base-scheduled" onClick={() => onEdit(scheduled, 'base-scheduled')}>
              {t('productEdit.pricesDrawer.base.edit')}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      {pendingOrNegative ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <HourglassTopRoundedIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {t(
              pendingOrNegative.state === 'REQUIRES_APPROVAL'
                ? 'productEdit.pricesDrawer.base.pending.requiresApproval'
                : pendingOrNegative.state === 'BLOCKED'
                  ? 'productEdit.pricesDrawer.base.pending.blocked'
                  : pendingOrNegative.state === 'REJECTED'
                    ? 'productEdit.pricesDrawer.base.pending.rejected'
                    : 'productEdit.pricesDrawer.base.pending.pending',
              { price: formatPrice(unitAmountOf(pendingOrNegative)) },
            )}
          </Typography>
          {suspicionLabel(t, pendingOrNegative.suspicionReason, pendingOrNegative.reason) ? (
            <Typography variant="body2" color="text.secondary">
              {t('productEdit.pricesDrawer.base.reason', {
                reason: suspicionLabel(t, pendingOrNegative.suspicionReason, pendingOrNegative.reason),
              })}
            </Typography>
          ) : null}
          {pendingOrNegative.state === 'REQUIRES_APPROVAL' ? (
            <MuiLink component={RouterLink} to={approvalsHref(productId)} variant="body2">
              {t('productEdit.pricesDrawer.base.openApprovals')}
            </MuiLink>
          ) : pendingOrNegative.state === 'BLOCKED' || pendingOrNegative.state === 'REJECTED' ? (
            <MuiLink component={RouterLink} to={auditHref(productId)} variant="body2">
              {t('productEdit.pricesDrawer.base.openAudit')}
            </MuiLink>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

// ── Section: "Other prices" (design spec §8.5) ──────────────────────────────────────────────────

function OtherPricesSection({
  lines,
  channels,
  lists,
  groups,
  canManage,
  onEdit,
}: {
  lines: PriceLineResponse[];
  channels: SalesChannelResponse[];
  lists: PriceListResponse[];
  groups: CustomerGroupResponse[];
  canManage: boolean;
  onEdit: (context: PriceCellEditorContext, target: PriceLineResponse | undefined, title: string, triggerId: string) => void;
}) {
  const { t } = useTranslation('catalog');
  const now = new Date();
  const groupsMap = groupBySlot(lines.filter((line) => !isBaseSlot(line)));
  // R0-class fix (Phase-3 review F1): the governing/scheduled/pending/negative derivation for each
  // non-base slot MUST come from `lib/priceSlots.ts` — the single source of truth for spec §8.0 —
  // never re-implemented inline here. The inline `governing` sort this used to do dropped the
  // `createdAt` residual tiebreak `byValidityDesc` applies, and `negativeLine` was never called at
  // all, so a slot whose only lines were BLOCKED/REJECTED rendered a silent blank Status cell.
  const rows = Array.from(groupsMap.values())
    .map((slotLines) => {
      const slot: PriceSlot = { ...slotLines[0] };
      const display = slotDisplay(slot, channels, lists, groups);
      const governing = governingLine(slotLines, slot, now);
      const scheduled = scheduledLine(slotLines, slot, now);
      const pending = pendingLine(slotLines, slot);
      const negative = negativeLine(slotLines, slot, governing);
      const primary = governing ?? scheduled ?? pending ?? negative ?? slotLines[0];
      return { slot, display, governing, scheduled, pending, negative, primary, rank: slotRank(slot) };
    })
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const nameOf = (r: typeof a) => r.display.listName ?? r.display.channelName ?? r.display.groupName ?? '';
      return nameOf(a).localeCompare(nameOf(b));
    });

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="overline" color="text.secondary" component="h3" sx={{ letterSpacing: '0.08em' }}>
          {t('productEdit.pricesDrawer.others.title')} ({rows.length})
        </Typography>
        <MuiLink component={RouterLink} to="/catalog/pricing?tab=grid" variant="body2">
          {t('productEdit.pricesDrawer.others.openGrid')}
        </MuiLink>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('productEdit.pricesDrawer.others.empty')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('productEdit.pricesDrawer.others.table.slot')}</TableCell>
                <TableCell>{t('productEdit.pricesDrawer.others.table.price')}</TableCell>
                <TableCell>{t('productEdit.pricesDrawer.others.table.validity')}</TableCell>
                <TableCell>{t('productEdit.pricesDrawer.others.table.status')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('productEdit.pricesDrawer.others.table.source')}
                </TableCell>
                <TableCell align="right">{t('productEdit.pricesDrawer.others.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ slot, display, governing, scheduled, pending, negative, primary }) => {
                const label = slotLabel(t, slot, display);
                const { context, title } = contextAndTitle(t, slot, display);
                const rowKey = `${slot.priceListCode ?? ''}-${slot.channelCode ?? ''}-${slot.customerGroupCode ?? ''}`;
                const triggerId = `slot-${rowKey}`;
                const sourceLabel = primary.batchId
                  ? t('productEdit.pricesDrawer.others.source.batch')
                  : primary.priceKind === 'MARKDOWN'
                    ? t('productEdit.pricesDrawer.others.source.markdown')
                    : t('productEdit.pricesDrawer.others.source.manual');
                const sourceTooltip = primary.batchId
                  ? t('productEdit.pricesDrawer.others.source.batchTooltip', { id: shortId(primary.batchId) })
                  : primary.createdBy
                    ? t('productEdit.pricesDrawer.others.source.authorTooltip', { author: primary.createdBy })
                    : sourceLabel;
                return (
                  <TableRow key={rowKey} hover>
                    <TableCell>
                      <Typography variant="body2">{label}</Typography>
                    </TableCell>
                    <TableCell>
                      {governing ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {formatPrice(unitAmountOf(governing))}
                          </Typography>
                          <TiersChip line={governing} />
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        {governing ? (
                          <Typography variant="body2">{validityText(t, governing)}</Typography>
                        ) : null}
                        {scheduled ? (
                          <Typography variant="caption" color="info.main">
                            {t('productEdit.summary.price.scheduled', {
                              date: formatDateTime(scheduled.validFrom),
                              price: formatPrice(unitAmountOf(scheduled)),
                            })}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {governing ? <Chip size="small" label={governing.state} color={stateColor(governing.state)} /> : null}
                        {pending ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={
                              pending.state === 'REQUIRES_APPROVAL'
                                ? t('productEdit.pricesDrawer.others.pendingChip.requiresApproval')
                                : t('productEdit.pricesDrawer.others.pendingChip.pending')
                            }
                          />
                        ) : null}
                        {negative ? (
                          // Governance must never be silent (domain-pricing): a slot whose only
                          // line is BLOCKED/REJECTED still gets a visible chip + reason tooltip,
                          // never a blank Status cell (design spec §8.5, this section's own R0-class
                          // gap — F1).
                          <Tooltip title={suspicionLabel(t, negative.suspicionReason, negative.reason) ?? ''}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={negative.state === 'BLOCKED' ? 'error' : 'default'}
                              label={
                                negative.state === 'BLOCKED'
                                  ? t('productEdit.pricesDrawer.others.pendingChip.blocked')
                                  : t('productEdit.pricesDrawer.others.pendingChip.rejected')
                              }
                            />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title={sourceTooltip}>
                        {primary.batchId ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={sourceLabel}
                            component={RouterLink}
                            to={batchHref(primary.batchId)}
                            clickable
                          />
                        ) : (
                          <Chip size="small" variant="outlined" label={sourceLabel} />
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t('productEdit.pricesDrawer.others.openInGrid')}>
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to={gridHref(slot)}
                            aria-label={t('productEdit.pricesDrawer.others.openInGrid')}
                          >
                            <GridOnRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage ? (
                          <IconButton
                            size="small"
                            data-edit-trigger-id={triggerId}
                            aria-label={t('productEdit.pricesDrawer.others.editAria', { slot: label })}
                            onClick={() => onEdit(context, primary === governing ? undefined : primary, title, triggerId)}
                          >
                            <SellRoundedIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

// ── Section: "Check which price applies" (design spec §8.6) ────────────────────────────────────

function resolutionSourceLabel(
  t: (key: string, vars?: Record<string, unknown>) => string,
  data: { priceListCodeUsed?: string; channelCodeUsed?: string; groupCodeUsed?: string },
  names: { list?: string; channel?: string; group?: string },
): string {
  if (data.priceListCodeUsed) return t('productEdit.pricesDrawer.others.slot.list', { name: names.list });
  if (data.channelCodeUsed) return t('productEdit.pricesDrawer.others.slot.channel', { name: names.channel });
  if (data.groupCodeUsed) return t('productEdit.pricesDrawer.others.slot.group', { name: names.group });
  return t('productEdit.pricesDrawer.base.title');
}

function ResolutionCheckSection({
  productId,
  channels,
  lists,
  groups,
  scheduledAt,
  storesEnabled,
}: {
  productId: string;
  channels: SalesChannelResponse[];
  lists: PriceListResponse[];
  groups: CustomerGroupResponse[];
  scheduledAt?: string;
  storesEnabled: boolean;
}) {
  const { t } = useTranslation('catalog');
  const [expanded, setExpanded] = useState(false);
  const [channelCode, setChannelCode] = useState(NONE_VALUE);
  const [storeCode, setStoreCode] = useState(NONE_VALUE);
  const [groupCode, setGroupCode] = useState(NONE_VALUE);
  const [at, setAt] = useState('');
  const [checked, setChecked] = useState<{ channelCode?: string; storeCode?: string; customerGroupCode?: string; at?: string } | null>(
    null,
  );

  const storesQuery = useRetailStores({ enabled: expanded && storesEnabled });
  const stores = storesEnabled && !storesQuery.isError ? (storesQuery.data ?? []) : [];

  const resolution = usePriceResolution(checked ? { productId, ...checked } : undefined, {
    enabled: Boolean(checked),
  });

  const toIso = (local: string): string | undefined => {
    if (!local.trim()) return undefined;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  };

  return (
    <Stack spacing={1}>
      {/* The disclosure trigger doubles as this section's heading (design spec §13: "section
          overlines are headings") — wrapping it in an h3 lets screen-reader users navigate to it
          by heading level exactly like the other three sections, while still exposing the
          standard button/aria-expanded disclosure semantics. */}
      <Typography component="h3" sx={{ m: 0 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="prices-drawer-check-panel"
          endIcon={
            <ExpandMoreRoundedIcon
              fontSize="small"
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          }
          sx={{ alignSelf: 'flex-start', pl: 0 }}
        >
          {t('productEdit.pricesDrawer.check.title')}
        </Button>
      </Typography>
      <Collapse in={expanded} id="prices-drawer-check-panel">
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label={t('productEdit.pricesDrawer.check.channel')}
              value={channelCode}
              onChange={(e) => setChannelCode(e.target.value)}
              fullWidth
            >
              <MenuItem value={NONE_VALUE}>{t('productEdit.pricesDrawer.check.none')}</MenuItem>
              {channels.filter((c) => c.active).map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            {stores.length > 0 ? (
              <TextField
                select
                size="small"
                label={t('productEdit.pricesDrawer.check.store')}
                value={storeCode}
                onChange={(e) => setStoreCode(e.target.value)}
                fullWidth
              >
                <MenuItem value={NONE_VALUE}>{t('productEdit.pricesDrawer.check.none')}</MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {groups.length > 0 ? (
              <TextField
                select
                size="small"
                label={t('productEdit.pricesDrawer.check.group')}
                value={groupCode}
                onChange={(e) => setGroupCode(e.target.value)}
                fullWidth
              >
                <MenuItem value={NONE_VALUE}>{t('productEdit.pricesDrawer.check.none')}</MenuItem>
                {groups.filter((g) => g.active).map((g) => (
                  <MenuItem key={g.code} value={g.code}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              type="datetime-local"
              label={t('productEdit.pricesDrawer.check.at')}
              value={at}
              onChange={(e) => setAt(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            {scheduledAt ? (
              <Button
                size="small"
                onClick={() => {
                  const d = new Date(scheduledAt);
                  const off = d.getTimezoneOffset();
                  setAt(new Date(d.getTime() - off * 60000).toISOString().slice(0, 16));
                }}
              >
                {t('productEdit.pricesDrawer.check.atScheduled', { date: formatDateTime(scheduledAt) })}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setChecked({
                  channelCode: channelCode !== NONE_VALUE ? channelCode : undefined,
                  storeCode: storeCode !== NONE_VALUE ? storeCode : undefined,
                  customerGroupCode: groupCode !== NONE_VALUE ? groupCode : undefined,
                  at: toIso(at),
                })
              }
            >
              {t('productEdit.pricesDrawer.check.show')}
            </Button>
          </Stack>

          {!checked ? (
            <Typography variant="body2" color="text.secondary">
              {t('productEdit.pricesDrawer.check.idle')}
            </Typography>
          ) : resolution.isLoading ? (
            <LoadingState label={t('productEdit.pricesDrawer.loading')} fullHeight={false} />
          ) : resolution.isError ? (
            resolution.error?.status === 404 ? (
              <Alert severity="info">{t('productEdit.pricesDrawer.check.notFound')}</Alert>
            ) : (
              <ErrorState message={resolution.error?.message} onRetry={() => void resolution.refetch()} />
            )
          ) : resolution.data ? (
            <Alert severity="success" icon={false} sx={{ bgcolor: 'action.hover' }}>
              {t(checked.storeCode ? 'productEdit.pricesDrawer.check.resultStore' : 'productEdit.pricesDrawer.check.result', {
                store: checked.storeCode,
                amount: formatPrice(resolution.data.unitAmount),
                source: resolutionSourceLabel(t, resolution.data, {
                  list: lists.find((l) => l.code === resolution.data?.priceListCodeUsed)?.name,
                  channel: channels.find((c) => c.code === resolution.data?.channelCodeUsed)?.name,
                  group: groups.find((g) => g.code === resolution.data?.groupCodeUsed)?.name,
                }),
              })}
            </Alert>
          ) : null}
        </Stack>
      </Collapse>
    </Stack>
  );
}

// ── Section: "History" (design spec §8.7) ───────────────────────────────────────────────────────

function HistorySection({ productId, open }: { productId: string; open: boolean }) {
  const { t } = useTranslation('catalog');
  const auditQuery = usePriceLifecycleAudit({ productId, page: 0, size: 1 }, { enabled: open });
  const last = auditQuery.data?.content?.[0];

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="overline" color="text.secondary" component="h3" sx={{ letterSpacing: '0.08em' }}>
          {t('productEdit.pricesDrawer.history.title')}
        </Typography>
        <MuiLink component={RouterLink} to={auditHref(productId)} variant="body2">
          {t('productEdit.pricesDrawer.history.open')}
        </MuiLink>
      </Stack>
      {auditQuery.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          …
        </Typography>
      ) : last ? (
        <Typography variant="body2" color="text.secondary">
          {t('productEdit.pricesDrawer.history.last', {
            date: formatDateTime(last.createdAt),
            action: last.action,
            actor: last.actor ?? '—',
          })}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('productEdit.pricesDrawer.history.none')}
        </Typography>
      )}
    </Stack>
  );
}

// ── Main drawer ──────────────────────────────────────────────────────────────────────────────────

/**
 * The card's Prices-360 (adr-merchant-portal-018 Decision 1) — a left-anchored `ProductSectionDrawer`
 * -family drawer (560px) with two levels, the `VersionHistoryDrawer` list→detail idiom: level 1 is
 * the read-heavy overview (§8.2–§8.7 sections below); level 2 is `PriceLineEditorForm` for whichever
 * slot the merchant chose, reached with a back control inside the SAME drawer.
 */
export function ProductPricesDrawer({ open, onClose, product, canManage, onResult }: ProductPricesDrawerProps) {
  const { t } = useTranslation('catalog');
  const { hasPermission } = usePermissions();
  const [level, setLevel] = useState<Level>({ view: 'overview' });
  // The invoking button's STABLE id (a `data-edit-trigger-id` attribute), not the DOM node itself:
  // level 1's whole subtree unmounts while level 2 is showing (design spec's own "one drawer
  // content at a time"), so a raw element ref would point at a detached node by the time the
  // merchant comes back — re-querying the FRESH node by id is what actually works.
  const lastTriggerId = useRef<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Reset to the overview whenever the drawer (re)opens — including for a different product.
  useEffect(() => {
    if (open) setLevel({ view: 'overview' });
  }, [open, product.id]);

  // Focus management (design spec §13): entering the editor focuses its first field; returning to
  // the overview restores focus to the button that opened the editor.
  useEffect(() => {
    if (level.view === 'editor') {
      bodyRef.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    } else if (lastTriggerId.current) {
      bodyRef.current
        ?.querySelector<HTMLElement>(`[data-edit-trigger-id="${lastTriggerId.current}"]`)
        ?.focus();
    }
  }, [level.view]);

  const linesQuery = useProductPriceLines(product.id, { enabled: open });
  const lines = linesQuery.data ?? [];
  const channelsQuery = useSalesChannels({ enabled: open });
  const listsQuery = usePriceLists({ enabled: open });
  const groupsQuery = useCustomerGroups({ enabled: open });
  const channels = channelsQuery.data ?? [];
  const lists = listsQuery.data ?? [];
  const groups = groupsQuery.data ?? [];
  const canViewStores = hasPermission('inventory.view');

  const openEditor = (context: PriceCellEditorContext, target: PriceLineResponse | undefined, title: string, triggerId: string) => {
    lastTriggerId.current = triggerId;
    setLevel({ view: 'editor', context, targetLine: target, title });
  };

  const scheduledBase = summarizeBase(lines, new Date()).scheduled;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100vw', sm: 560 }, display: 'flex', flexDirection: 'column' } } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {level.view === 'editor' ? (
          <IconButton
            size="small"
            onClick={() => setLevel({ view: 'overview' })}
            aria-label={t('productEdit.pricesDrawer.back')}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        ) : (
          <SellRoundedIcon color="action" aria-hidden />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }} noWrap>
            {level.view === 'editor' ? `${level.title} — ${product.name ?? product.id}` : t('productEdit.drawers.prices.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {level.view === 'editor' ? product.name ?? product.id : t('productEdit.drawers.prices.subtitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('common:action.close')}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {level.view === 'editor' ? (
          <PriceLineEditorForm
            product={product}
            context={level.context}
            existingLines={lines}
            targetLine={level.targetLine}
            readOnly={!canManage}
            onSaved={(message) => {
              onResult('success', message);
              setLevel({ view: 'overview' });
            }}
            onCancel={() => setLevel({ view: 'overview' })}
          />
        ) : (
          <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
            {!canManage ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('productEdit.pricesDrawer.readOnlyNotice')}
              </Alert>
            ) : null}
            {linesQuery.isLoading ? (
              <LoadingState label={t('productEdit.pricesDrawer.loading')} />
            ) : linesQuery.isError ? (
              <ErrorState
                title={t('productEdit.pricesDrawer.loadError.title')}
                message={linesQuery.error?.message ?? t('common:error.unknown')}
                onRetry={() => void linesQuery.refetch()}
              />
            ) : (
              <Stack spacing={2.5} divider={<Divider />}>
                <BaseSection
                  lines={lines}
                  canManage={canManage}
                  productId={product.id}
                  onEdit={(target, triggerId) => openEditor({ type: 'base' }, target, t('productEdit.pricesDrawer.base.title'), triggerId)}
                />
                <OtherPricesSection
                  lines={lines}
                  channels={channels}
                  lists={lists}
                  groups={groups}
                  canManage={canManage}
                  onEdit={openEditor}
                />
                <ResolutionCheckSection
                  productId={product.id}
                  channels={channels}
                  lists={lists}
                  groups={groups}
                  scheduledAt={scheduledBase?.validFrom}
                  storesEnabled={canViewStores}
                />
                <HistorySection productId={product.id} open={open} />
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
